import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import isEqual from "lodash/isEqual";
import { Modal } from "@shopify/polaris";
import { FormFunnel } from "components/form-funnel";
import { FunnelConfig } from "components/form-funnel/types";
import {
  HR_PREFILL_FIELDS,
  hrPrefillSteps,
  step2InitialData,
} from "containers/my-onboarding/onboarding-configs";
import { fetchOnboardingById, prefillEmployeeForm } from "services/onboarding.service";
import { Onboarding } from "types/Onboarding";
import { isEmployeeFormLocked } from "utils/onboarding-errors";

/**
 * HR may fill the employee form while it is still the employee's to submit:
 * the step_2 form is open and not submitted yet (so the record is not
 * terminal either). HR only — Master or 'Access onboarding', not a hiring head.
 */
export function canPrefillEmployeeForm(ob: Onboarding, canManageOnboarding: boolean): boolean {
  return canManageOnboarding && ob.status === "step_2" && !ob.employeeSubmitted;
}

/**
 * The HR fields HR changed in this dialog. The employee may be filling the
 * same form meanwhile: sending untouched fields would overwrite their newer
 * answers with the copy this dialog started from. The children list goes
 * only when it was edited.
 */
export function changedHrFields(
  initial: Record<string, any>,
  data: Record<string, any>
): Record<string, any> {
  const changed: Record<string, any> = {};
  for (const field of HR_PREFILL_FIELDS) {
    if (data[field] !== undefined && !isEqual(data[field], initial[field])) {
      changed[field] = data[field];
    }
  }
  return changed;
}

interface EmployeeFormPrefillProps {
  companyId: string;
  onboardingId: string;
  /** Called after the dialog closed and its last save finished, to reload the record. */
  onClosed: () => void;
}

/**
 * HR fills the employee form with the data they already hold; the employee
 * then checks, completes, submits and signs it after logging in. The form
 * starts from a fresh copy of the record; changes are autosaved, and the last
 * step's button saves and closes.
 */
export default function EmployeeFormPrefill({ companyId, onboardingId, onClosed }: EmployeeFormPrefillProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Initial form values, from the record fetched when the dialog opened.
  const [initialData, setInitialData] = useState<Record<string, any> | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // The employee submitted meanwhile: the form can no longer be changed.
  const [locked, setLocked] = useState(false);
  const [closing, setClosing] = useState(false);
  const pendingSaves = useRef(new Set<Promise<void>>());
  // Bumped on every open and close: a fetch answering a dialog already closed is dropped.
  const openingRef = useRef(0);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  const openDialog = async () => {
    const opening = ++openingRef.current;
    setOpen(true);
    setInitialData(null);
    setLoadFailed(false);
    try {
      const fresh = await fetchOnboardingById(companyId, onboardingId);
      if (opening !== openingRef.current) return;
      if (!canPrefillEmployeeForm(fresh, true)) {
        setLocked(true);
      }
      setInitialData(step2InitialData(fresh));
    } catch {
      if (opening === openingRef.current) setLoadFailed(true);
    }
  };

  // Unmounting the funnel (below) sends its pending autosave; the page reloads
  // once that and every other save of this dialog has finished.
  const close = () => {
    openingRef.current++;
    setOpen(false);
    setInitialData(null);
    setLocked(false);
    setClosing(true);
  };
  useEffect(() => {
    if (!closing) return;
    Promise.allSettled(Array.from(pendingSaves.current)).then(() => {
      setClosing(false);
      onClosedRef.current();
    });
  }, [closing]);

  const save = async (data: Record<string, any>) => {
    const changed = changedHrFields(initialData!, data);
    if (Object.keys(changed).length === 0) return;
    try {
      await prefillEmployeeForm(companyId, onboardingId, changed);
    } catch (e) {
      if (isEmployeeFormLocked(e)) setLocked(true);
      throw e;
    }
  };

  const funnelConfig: FunnelConfig | null = initialData && {
    id: "onboarding-employee-form-prefill",
    autoSave: true,
    steps: hrPrefillSteps,
    initialData,
    submitLabel: t("save"),
    onSave: (_stepId, data) => {
      const saving = save(data);
      pendingSaves.current.add(saving);
      saving.finally(() => pendingSaves.current.delete(saving)).catch(() => {});
      return saving;
    },
    // FormFunnel.handleNext saves the last step (onSave) before calling
    // onSubmit, so there is nothing left to send here.
    onSubmit: async () => close(),
  };

  let body: React.ReactNode = null;
  if (!open) {
    // Closing: the funnel is gone (its pending save was sent on unmount).
  } else if (locked) {
    body = (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg text-sm">
        {t("prefill_employee_form_locked")}
      </div>
    );
  } else if (loadFailed) {
    body = <p className="text-sm text-red-600">{t("error")}</p>;
  } else if (!funnelConfig) {
    body = <p className="text-sm text-gray-500">{t("loading")}</p>;
  } else {
    body = (
      <>
        <p className="mb-4 text-sm text-gray-600">{t("prefill_employee_form_note")}</p>
        <FormFunnel config={funnelConfig} />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
        onClick={openDialog}
        disabled={closing}
      >
        {t("prefill_employee_form")}
      </button>
      {/* A Polaris Modal, not the Headless UI dialog: the form's date pickers
          open Polaris popovers, which a Headless dialog hides under itself and
          blocks from taps. Mounted only while open: Polaris keeps an exiting
          modal's content alive for its fade-out, which would delay the funnel's
          unmount save past the reload. */}
      {open && (
        <Modal open onClose={close} title={t("prefill_employee_form")} size="large" sectioned>
          {body}
        </Modal>
      )}
    </>
  );
}
